// =============================================================================
// PEDRA — src/components/Header.tsx
// Barra superior do aplicativo: marca, nome do projeto, menus de arquivo e de
// importacao/exportacao, desfazer/refazer, compartilhar, imprimir, atalhos,
// configuracoes, alternador de tema e indicador de gravacao automatica.
//
// O Header nao acessa o estado global: tudo chega por props do App. Os seletores
// de arquivo (<input type="file">) moram no App, por isso aqui so existem os
// gatilhos (`onOpenProject`, `onImportCsv`).
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement, ReactNode } from 'react';

import type { SaveStatus } from '../state/persistence';

// -----------------------------------------------------------------------------
// TIPOS
// -----------------------------------------------------------------------------

/** Alvos aceitos pela exportacao em CSV. */
export type CsvTarget = 'parts' | 'stock' | 'cuts' | 'offcuts';

/** Alvos aceitos pela importacao em CSV. */
export type CsvImportTarget = 'parts' | 'stock';

export interface HeaderProps {
  projectName: string;
  onRenameProject(name: string): void;
  onNewProject(): void;
  onLoadSample(): void;
  /** Abre o seletor de arquivo de projeto (.json). O input mora no App. */
  onOpenProject(): void;
  /** Abre o seletor de CSV para peças ou chapas. */
  onImportCsv(target: CsvImportTarget): void;
  onExportJson(): void;
  onExportCsv(target: CsvTarget): void;
  onShare(): void;
  onPrint(): void;
  onOpenSettings(): void;
  onOpenShortcuts(): void;
  onUndo(): void;
  onRedo(): void;
  canUndo: boolean;
  canRedo: boolean;
  saveStatus: SaveStatus;
  theme: 'light' | 'dark';
  onToggleTheme(): void;
  onToggleSidebar(): void;
  sidebarOpen: boolean;
  /** Erros bloqueantes do projeto — vira o selo vermelho do botão de entradas. */
  errorCount: number;
  /** true quando já existe um plano de corte calculado. */
  hasResult: boolean;
}

// -----------------------------------------------------------------------------
// MENU SUSPENSO (sem biblioteca)
// -----------------------------------------------------------------------------

interface HeaderMenuProps {
  label: string;
  title: string;
  children: ReactNode;
}

/**
 * Menu simples: abre no clique, fecha com Esc, com clique fora ou ao acionar
 * qualquer item (o clique do item borbulha ate o container).
 */
function HeaderMenu(props: HeaderMenuProps): ReactElement {
  const { label, title, children } = props;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: MouseEvent): void => {
      const root = rootRef.current;
      if (root && !root.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="header-menu" ref={rootRef} onClick={() => setOpen(false)}>
      <button
        type="button"
        className={open ? 'btn btn--sm header-menu__btn is-open' : 'btn btn--sm header-menu__btn'}
        aria-haspopup="menu"
        aria-expanded={open}
        title={title}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        {label}
        <span className="header-menu__caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open ? (
        <ul className="header-menu__list" role="menu" aria-label={label}>
          {children}
        </ul>
      ) : null}
    </div>
  );
}

interface MenuItemProps {
  label: string;
  onSelect(): void;
  hint?: string;
  disabled?: boolean;
  title?: string;
}

function MenuItem(props: MenuItemProps): ReactElement {
  const { label, onSelect, hint, disabled, title } = props;
  return (
    <li role="none">
      <button
        type="button"
        role="menuitem"
        className="header-menu__item"
        onClick={onSelect}
        disabled={disabled === true}
        title={title ?? label}
      >
        <span className="header-menu__item-label">{label}</span>
        {hint !== undefined ? <kbd className="header-menu__hint">{hint}</kbd> : null}
      </button>
    </li>
  );
}

function MenuSeparator(): ReactElement {
  return <li className="header-menu__sep" role="separator" />;
}

// -----------------------------------------------------------------------------
// INDICADOR DE GRAVACAO
// -----------------------------------------------------------------------------

const SAVE_LABEL: Readonly<Record<SaveStatus, string>> = {
  idle: 'Rascunho local',
  saving: 'Salvando…',
  saved: 'Salvo neste navegador',
  error: 'Falha ao salvar',
};

const SAVE_SHORT: Readonly<Record<SaveStatus, string>> = {
  idle: '—',
  saving: 'Salvando…',
  saved: 'Salvo',
  error: 'Erro ao salvar',
};

function SaveIndicator(props: { status: SaveStatus }): ReactElement {
  const { status } = props;
  const cls =
    status === 'error'
      ? 'header-save is-error'
      : status === 'saving'
        ? 'header-save is-saving'
        : status === 'saved'
          ? 'header-save is-saved'
          : 'header-save';
  return (
    <span className={cls} role="status" aria-live="polite" title={SAVE_LABEL[status]}>
      <span className="header-save__dot" aria-hidden="true" />
      <span className="header-save__text">{SAVE_SHORT[status]}</span>
    </span>
  );
}

// -----------------------------------------------------------------------------
// COMPONENTE
// -----------------------------------------------------------------------------

export function Header(props: HeaderProps): ReactElement {
  const {
    projectName,
    onRenameProject,
    onNewProject,
    onLoadSample,
    onOpenProject,
    onImportCsv,
    onExportJson,
    onExportCsv,
    onShare,
    onPrint,
    onOpenSettings,
    onOpenShortcuts,
    onUndo,
    onRedo,
    canUndo,
    canRedo,
    saveStatus,
    theme,
    onToggleTheme,
    onToggleSidebar,
    sidebarOpen,
    errorCount,
    hasResult,
  } = props;

  // O nome so entra no historico quando o campo perde o foco (ou no Enter):
  // gravar a cada tecla encheria a pilha de desfazer.
  const [draft, setDraft] = useState(projectName);
  const editingRef = useRef(false);

  useEffect(() => {
    if (!editingRef.current) setDraft(projectName);
  }, [projectName]);

  const commitName = useCallback((): void => {
    editingRef.current = false;
    const clean = draft.trim();
    if (clean !== projectName) onRenameProject(clean);
    setDraft(clean === '' ? projectName : clean);
  }, [draft, projectName, onRenameProject]);

  return (
    <header className="header-root app-header no-print">
      <button
        type="button"
        className="icon-btn header-burger"
        onClick={onToggleSidebar}
        aria-expanded={sidebarOpen}
        aria-controls="pedra-sidebar"
        title={sidebarOpen ? 'Ocultar as entradas (Ctrl+B)' : 'Mostrar as entradas (Ctrl+B)'}
      >
        <span aria-hidden="true">☰</span>
        <span className="sr-only">Mostrar ou ocultar as entradas</span>
        {errorCount > 0 ? (
          <span className="header-burger__badge" aria-hidden="true">
            {errorCount > 9 ? '9+' : errorCount}
          </span>
        ) : null}
      </button>

      <div className="header-brand">
        <span className="header-brand__mark" aria-hidden="true">
          ▣
        </span>
        <span className="header-brand__name">Otimizador de Corte</span>
      </div>

      <label className="header-name">
        <span className="sr-only">Nome do projeto</span>
        <input
          className="input header-name__input"
          type="text"
          value={draft}
          maxLength={80}
          placeholder="Nome do projeto"
          title="Nome do projeto — usado nos arquivos exportados"
          onFocus={() => {
            editingRef.current = true;
          }}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitName}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.currentTarget.blur();
            } else if (e.key === 'Escape') {
              editingRef.current = false;
              setDraft(projectName);
              e.currentTarget.blur();
            }
          }}
        />
      </label>

      <nav className="header-actions" aria-label="Ações do projeto">
        <HeaderMenu label="Projeto" title="Novo, abrir, salvar e exemplo">
          <MenuItem label="Novo projeto" hint="Ctrl+N" onSelect={onNewProject} />
          <MenuItem label="Abrir projeto…" hint="Ctrl+O" onSelect={onOpenProject} />
          <MenuItem label="Salvar projeto (.json)" hint="Ctrl+S" onSelect={onExportJson} />
          <MenuSeparator />
          <MenuItem
            label="Carregar exemplo"
            title="Substitui o conteúdo atual por um projeto de exemplo"
            onSelect={onLoadSample}
          />
        </HeaderMenu>

        <HeaderMenu label="Dados" title="Importar e exportar planilhas CSV">
          <MenuItem label="Importar peças (CSV)…" onSelect={() => onImportCsv('parts')} />
          <MenuItem label="Importar chapas (CSV)…" onSelect={() => onImportCsv('stock')} />
          <MenuSeparator />
          <MenuItem label="Exportar peças (CSV)" onSelect={() => onExportCsv('parts')} />
          <MenuItem label="Exportar chapas (CSV)" onSelect={() => onExportCsv('stock')} />
          <MenuItem
            label="Exportar cortes (CSV)"
            disabled={!hasResult}
            title={hasResult ? 'Sequência de cortes do último cálculo' : 'Calcule o plano antes'}
            onSelect={() => onExportCsv('cuts')}
          />
          <MenuItem
            label="Exportar sobras (CSV)"
            disabled={!hasResult}
            title={hasResult ? 'Retalhos aproveitáveis do último cálculo' : 'Calcule o plano antes'}
            onSelect={() => onExportCsv('offcuts')}
          />
        </HeaderMenu>

        <span className="header-sep" aria-hidden="true" />

        <button
          type="button"
          className="icon-btn"
          onClick={onUndo}
          disabled={!canUndo}
          title="Desfazer (Ctrl+Z)"
        >
          <span aria-hidden="true">↶</span>
          <span className="sr-only">Desfazer</span>
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={onRedo}
          disabled={!canRedo}
          title="Refazer (Ctrl+Shift+Z)"
        >
          <span aria-hidden="true">↷</span>
          <span className="sr-only">Refazer</span>
        </button>

        <span className="header-sep" aria-hidden="true" />

        <button
          type="button"
          className="icon-btn"
          onClick={onShare}
          title="Gerar link compartilhável deste projeto"
        >
          <span aria-hidden="true">🔗</span>
          <span className="sr-only">Compartilhar link</span>
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={onPrint}
          disabled={!hasResult}
          title={hasResult ? 'Imprimir ou salvar em PDF (Ctrl+P)' : 'Calcule o plano antes de imprimir'}
        >
          <span aria-hidden="true">🖨</span>
          <span className="sr-only">Imprimir</span>
        </button>
        <button
          type="button"
          className="icon-btn header-hide-sm"
          onClick={onOpenShortcuts}
          title="Atalhos de teclado (Shift+?)"
        >
          <span aria-hidden="true">?</span>
          <span className="sr-only">Atalhos de teclado</span>
        </button>
        <button type="button" className="icon-btn" onClick={onOpenSettings} title="Configurações">
          <span aria-hidden="true">⚙</span>
          <span className="sr-only">Configurações</span>
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={onToggleTheme}
          aria-pressed={theme === 'dark'}
          title={theme === 'dark' ? 'Usar tema claro' : 'Usar tema escuro'}
        >
          <span aria-hidden="true">{theme === 'dark' ? '☀' : '☾'}</span>
          <span className="sr-only">Alternar tema</span>
        </button>
      </nav>

      <SaveIndicator status={saveStatus} />
    </header>
  );
}

export default Header;
